import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from './dialog';
import { Button } from './button';
import { Trash2 } from 'lucide-react';

export interface DeleteItem {
  id: string | number;
  name?: string;
  type?: string;
  fileName?: string;
  uploadDate?: string;
  [key: string]: any; // Allow additional properties
}

export interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title?: string;
  isBulkDelete?: boolean;
  itemToDelete?: any | null;
  selectedItems?: any[];
  itemType?: string; // e.g., "Material", "Specification", "Pipe", etc.
  itemName?: string; // e.g., "Pipe 1", "Material ABC", etc.
  warningMessage?: string;
  detailsRenderer?: (item: DeleteItem) => React.ReactNode;
  maxWidth?: string;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  title,
  isBulkDelete = false,
  itemToDelete,
  selectedItems = [],
  itemType = 'Item',
  itemName,
  warningMessage,
  detailsRenderer,
  maxWidth = 'max-w-[800px]'
}) => {
  const getDefaultTitle = () => {
    if (isBulkDelete) {
      return `Delete ${itemType}s`;
    }
    return `Delete ${itemType}`;
  };

  const getDefaultWarningMessage = () => {
    if (isBulkDelete) {
      return `All ${itemType.toLowerCase()} data will be permanently removed from the system.`;
    }
    return `This ${itemType.toLowerCase()} data  will be permanently removed from the system.`;
  };

  const renderItemDetails = (item: DeleteItem) => {
    if (detailsRenderer) {
      return detailsRenderer(item);
    }


    return (
      <div className="bg-page-bg border border-[#E2E5EA] p-3 rounded-md">
        <p className="text-[12px] text-heritage-grey mb-2 font-medium">{itemType} details:</p>
        <div className="space-y-1">
          {item.type && (
            <p className="text-[13px] text-text-grey flex items-center">
              <span className="w-1.5 h-1.5 bg-heritage-grey rounded-full mr-2"></span>
              Type: {item.type}
            </p>
          )}
          {item.fileName && (
            <p className="text-[13px] text-text-grey flex items-center">
              <span className="w-1.5 h-1.5 bg-heritage-grey rounded-full mr-2"></span>
              File Name: {item.fileName}
            </p>
          )}
          {item.uploadDate && (
            <p className="text-[13px] text-text-grey flex items-center">
              <span className="w-1.5 h-1.5 bg-heritage-grey rounded-full mr-2"></span>
              Upload Date: {new Date(item.uploadDate).toLocaleDateString()}
            </p>
          )}
          {item.name && (
            <p className="text-[13px] text-text-grey flex items-center">
              <span className="w-1.5 h-1.5 bg-heritage-grey rounded-full mr-2"></span>
              Name: {item.name}
            </p>
          )}
        </div>
      </div>
    );
  };

  if (itemToDelete && itemToDelete?.productionData && itemToDelete.productionData.isProductionAvailable && 
    (itemToDelete.materialStatus === 'Consumed' || itemToDelete.assemblyNumbers.length > 0)) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`${maxWidth} p-6`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-[rgba(235,28,55,0.1)] rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-hero-red" />
            </div>
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {title || getDefaultTitle()}
            </DialogTitle>
          </div>
          <div>
            <p className="text-[13px] text-text-grey mb-3">
            ⚠️ Cannot delete  the
                {itemName && <span className="font-semibold text-hero-red"> {itemName} </span>}
                {itemType.toLowerCase()}. this materials have been used in production?
              </p>


            <div className="mt-4 p-3 bg-hero-red-10 border border-hero-red-20 rounded-md">
              <p className="text-[13px] text-readable-red">
                <span className="font-semibold">Action Required:</span>
                <br />
                <span className="block mt-1">• Please remove all associated production data before deleting the materials.</span>

              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="min-w-[100px] border-[#E2E5EA] text-text-grey hover:bg-page-bg"
            >
              Cancel
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }




  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${maxWidth} p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[rgba(235,28,55,0.1)] rounded-full flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
            {title || getDefaultTitle()}
          </DialogTitle>
        </div>

        <div className="mb-6">
          {isBulkDelete ? (
            <>
              <p className="text-[13px] text-text-grey mb-3">
                Are you sure you want to <span className="font-semibold text-hero-red">delete</span> the
                {itemName && <span className="font-semibold text-hero-red"> {itemName} </span>}
                {itemType.toLowerCase()} documents?
              </p>
              <div className="bg-page-bg border border-[#E2E5EA] p-3 rounded-md">
                <p className="text-[12px] text-heritage-grey mb-2">Document items:</p>
                <div className="max-h-32 overflow-y-auto">
                  {selectedItems.map((item) => (
                    <div key={item.id} className="text-[13px] text-text-grey py-1">
                      {renderItemDetails(item)}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-text-grey mb-3">
                Are you sure you want to <span className="font-semibold text-hero-red">delete</span> the {itemType.toLowerCase()}
                {itemName && <span className="font-semibold text-hero-red"> {itemName} ? </span>}
              </p>
              {itemToDelete && (
                <>
                  <div className="text-[13px] text-text-grey mb-3 font-medium">Delete Details:</div>
                  {renderItemDetails(itemToDelete)}
                </>
              )}
            </>
          )}

          <div className="mt-4 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-md">
            <p className="text-[13px] text-hero-red">
              <span className="font-semibold">Warning:</span> This action is irreversible.
              {warningMessage || getDefaultWarningMessage()}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="min-w-[100px] border-[#E2E5EA] text-text-grey hover:bg-page-bg"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[100px]"
            loading={isLoading}
            loadingText="Deleting..."
          >
            {isBulkDelete ? 'Delete All' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 